<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-clock"></i>
        {{ $t("fleetReports.timetable_title") }}
      </h4>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("fleetReports.from_date") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="fromDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("common.ຫາວັນທີ") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="toDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetReports.route") }}</label>
            <model-list-select
                :list="boardRoutes"
                v-model="selectedRouteId"
                :placeholder="$t('common.ທັງໝົດ')"
                option-text="name"
                option-value="id"
                @input="FetchReport"
            >
            </model-list-select>
          </div>
        </div>
      </div>
    </div>

    <!-- Per-route/day aggregate -->
    <div class="center table-scroll">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("fleetReports.date") }}</vs-th>
            <vs-th>{{ $t("fleetReports.route") }}</vs-th>
            <vs-th>{{ $t("fleetReports.stops") }}</vs-th>
            <vs-th>{{ $t("fleetReports.arrived") }}</vs-th>
            <vs-th>{{ $t("fleetReports.missing") }}</vs-th>
            <vs-th>{{ $t("fleetReports.early") }}</vs-th>
            <vs-th>{{ $t("fleetReports.on_time") }}</vs-th>
            <vs-th>{{ $t("fleetReports.late") }}</vs-th>
            <vs-th>{{ $t("fleetReports.avg_variance_minutes") }}</vs-th>
            <vs-th>{{ $t("fleetReports.avg_dwell_minutes") }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" :data="row" v-for="(row, index) in aggregate">
            <vs-td>{{ FleetFormatDate(row.date) }}</vs-td>
            <vs-td :style="{color: '#195BFF'}">{{ row.route_name }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ row.stops_total }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ row.arrived }}</vs-td>
            <vs-td :style="{textAlign: 'center', color: row.missing ? '#9aa0a6' : ''}">{{ row.missing }}</vs-td>
            <vs-td :style="{textAlign: 'center', color: '#195BFF'}">{{ row.early }}</vs-td>
            <vs-td :style="{textAlign: 'center', color: '#2dcc70'}">{{ row.on_time }}</vs-td>
            <vs-td :style="{textAlign: 'center', color: row.late ? '#e8a10f' : ''}">{{ row.late }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ SignedMinutes(row.avg_arrival_delta_minutes) }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ NullableMinutes(row.avg_dwell_minutes) }}</vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Loading v-if="isLoadingReport"/>
          <p v-if="!isLoadingReport && !aggregate.length"
             :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
            {{ $t("fleetReports.no_data_in_range") }}
          </p>
        </template>
      </vs-table>
    </div>

    <!-- Per-trip per-stop detail -->
    <div v-if="trips.length" class="detail-wrap">
      <h5 :style="{margin: '18px 0 8px'}">{{ $t("fleetReports.per_trip_detail") }}</h5>
      <div v-for="trip in trips" :key="trip.trip_id" class="trip-card">
        <div class="trip-head">
          <span class="trip-code">{{ trip.trip_code }}</span>
          <span v-if="trip.direction"
                :style="{color: FleetDirectionColor(trip.direction), fontWeight: 700, fontSize: '12px'}">
            [{{ FleetDirectionLabel(trip.direction) }}]
          </span>
          <span v-if="trip.truck_plate" class="truck">
            <i class="fal fa-truck"></i> {{ trip.truck_plate }}
          </span>
          <span class="route">{{ trip.route_name }} · {{ $t("fleetReports.round_n", {n: trip.round_number}) }}</span>
          <span class="date">{{ FleetFormatDate(trip.service_date) }}</span>
        </div>
        <div class="table-scroll">
          <table class="stop-table">
            <thead>
            <tr>
              <th>#</th>
              <th>{{ $t("fleetReports.stops") }}</th>
              <th>{{ $t("fleetReports.planned") }}</th>
              <th>{{ $t("fleetReports.actual_arrival") }}</th>
              <th>{{ $t("fleetReports.variance") }}</th>
              <th>{{ $t("fleetReports.status") }}</th>
              <th>{{ $t("fleetReports.dwell_minutes") }}</th>
              <th>{{ $t("fleetReports.method") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="stop in trip.stops" :key="stop.trip_stop_id">
              <td>{{ stop.sequence }}</td>
              <td>{{ stop.stop_name || (stop.stop_type + ' #' + stop.stop_id) }}</td>
              <td>{{ FleetFormatDateTime(stop.planned_arrival_at) }}</td>
              <td>{{ FleetFormatDateTime(stop.checked_in_at) }}</td>
              <td>{{ SignedMinutes(stop.arrival_delta_minutes) }}</td>
              <td>
                <span class="state-badge" :style="{background: StateColor(stop.arrival_state)}">
                  {{ StateLabel(stop.arrival_state) }}
                </span>
              </td>
              <td>{{ NullableMinutes(stop.dwell_minutes) }}</td>
              <td>{{ MethodLabel(stop.checkin_method) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';

// arrival_state / checkin_method are API values -> i18n keys; only the label is translated.
const STATE_LABEL_KEYS = {
  early: 'fleetReports.early',
  on_time: 'fleetReports.on_time',
  late: 'fleetReports.late',
  missing: 'fleetReports.missing',
};
const STATE_COLORS = {early: '#195BFF', on_time: '#2dcc70', late: '#e8a10f', missing: '#9aa0a6'};
const METHOD_LABEL_KEYS = {geofence: 'fleetReports.method_geofence', manual: 'fleetReports.method_manual'};

/**
 * US3 (001-fleet-gps-tracking): planned-vs-actual timetable variance report.
 * Per-route/day aggregate on top, per-trip per-stop detail below. Self-hides
 * gracefully on an old backend (404 → empty state, no error spam).
 */
export default {
  name: 'FleetTimetableVariance',
  components: {
    DatePicker,
    ModelListSelect,
    Loading,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    // Default to TODAY (start = end) — the timetable is a day-of-operations
    // view; a month-wide default buried the current day's trips.
    fromDate: moment(new Date()).format('YYYY-MM-DD'),
    toDate: moment(new Date()).format('YYYY-MM-DD'),
    selectedRouteId: '',
    aggregate: [],
    trips: [],
    isLoadingReport: false,
  }),
  methods: {
    StateLabel(state) {
      const key = STATE_LABEL_KEYS[state];
      return key ? this.$t(key) : (state || '—');
    },
    StateColor(state) {
      return STATE_COLORS[state] || '#9aa0a6';
    },
    MethodLabel(method) {
      const key = METHOD_LABEL_KEYS[method];
      return key ? this.$t(key) : '—';
    },
    SignedMinutes(value) {
      if (value === null || value === undefined) return '—';
      const n = Number(value);
      const sign = n > 0 ? '+' : '';
      return this.$t('fleetReports.minutes_value', {n: `${sign}${n}`});
    },
    NullableMinutes(value) {
      if (value === null || value === undefined) return '—';
      return this.$t('fleetReports.minutes_value', {n: value});
    },
    FetchReport() {
      this.isLoadingReport = true;
      this.FetchTimetableVariance({
        from: this.fromDate,
        to: this.toDate,
        route_id: this.selectedRouteId || undefined,
      }).then((res) => {
        const data = (res.data && res.data.data) ? res.data.data : {};
        this.aggregate = data.aggregate || [];
        this.trips = data.trips || [];
        this.isLoadingReport = false;
      }).catch((error) => {
        this.aggregate = [];
        this.trips = [];
        this.isLoadingReport = false;
        // Old backend (404) → quiet empty state; a real range error is surfaced.
        if (error && error.response && error.response.status !== 404) {
          this.$notification.error({message: this.FleetErrorText(error, this.$t('fleetReports.fetch_failed'))});
        }
      });
    },
  },
  created() {
    this.FetchBoardRoutes();
    this.FetchReport();
  },
};
</script>

<style scoped>
.table-scroll {
  overflow-x: auto;
}

.detail-wrap {
  margin-top: 10px;
}

.trip-card {
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.trip-head {
  display: flex;
  gap: 12px;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.trip-head .trip-code {
  font-weight: 700;
}

.trip-head .truck {
  background: #eef1f6;
  color: #384250;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
}

.trip-head .route {
  color: #195BFF;
}

.trip-head .date {
  color: #8a909a;
  font-size: 12px;
  margin-left: auto;
}

.stop-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stop-table th,
.stop-table td {
  border-bottom: 1px solid #f0f1f4;
  padding: 6px 8px;
  text-align: left;
  white-space: nowrap;
}

.stop-table th {
  color: #5a6472;
  font-weight: 600;
}

.state-badge {
  color: white;
  border-radius: 10px;
  padding: 1px 10px;
  font-size: 11px;
}
</style>
